import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HeartIcon, LandmarkIcon, BriefcaseIcon, PieChartIcon, CheckCircle2Icon, ArrowRightIcon } from 'lucide-react';
import { Container } from '../components/ui/Container';
import { Button } from '../components/ui/Button';
import { GeometricAccent } from '../components/ui/GeometricAccent';
import { PatternDecor } from '../components/ui/PatternDecor';
import { MosqueArchMask } from '../components/ui/MosqueArchMask';
export function Support() {
  return <>
      {/* Hero */}
      <section className="relative overflow-hidden isolate" data-id="element-4878" data-mp="byguvgm4cknb">
        <PatternDecor side="left" y="middle" size="lg" data-id="element-4879" data-mp="c0bemrlnm6pt" />
        <GeometricAccent variant="dots" className="absolute top-8 right-6 w-32 h-32" opacity={0.4} data-id="element-4880" data-mp="gxjvmov2g4ph" />
        <Container className="relative pt-8 md:pt-12 pb-16 md:pb-24" data-id="element-4881" data-mp="1nd3zrjjxvwww">
          <div className="grid lg:grid-cols-12 gap-10 items-center" data-id="element-4882" data-mp="1sb5lydcnwccl">
            <div className="lg:col-span-6" data-id="element-4883" data-mp="21xngn57r0631">
              <p className="text-forest-700 font-semibold mb-4" data-id="element-4884" data-mp="21wluo5p71z6j">
                Strategic Support for Muslim Families, Education & Long-Term
                Stability
              </p>
              <h1 className="font-display text-4xl md:text-6xl text-ink-900 leading-[1.05]" data-id="element-4885" data-mp="1rxmrh2n76vqx">
                Partnering For
                <br data-id="element-4886" data-mp="1n1xpytrmb4rz" />
                Long-Term Impact
              </h1>
              <p className="mt-6 text-ink-700 max-w-xl leading-relaxed" data-id="element-4887" data-mp="1iz1ctikuk0vf">
                International Islamic Learning Hub is a mission-driven
                educational and family support initiative focused on{' '}
                <strong className="text-ink-900" data-id="element-4888" data-mp="198pwewjz7z7n">
                  protecting Muslim children and families living as minorities
                </strong>
                , while promoting stability, dignity, and responsible
                integration.
              </p>
              <p className="mt-4 text-ink-700 max-w-xl leading-relaxed" data-id="element-4889" data-mp="v9q9pug0xue4">
                We work with strategic donors, foundations, and institutional
                partners who share a long-term vision for{' '}
                <strong className="text-ink-900" data-id="element-4890" data-mp="po7w6h76xk8d">
                  education, identity preservation, and social cohesion.
                </strong>
              </p>
              <div className="mt-8 flex flex-wrap gap-3" data-id="element-4891" data-mp="glgjke2kwif5">
                <Link to="/en/partnership/donate-for-children" data-id="element-4892" data-mp="go2qcbhnatwb">
                  <Button variant="primary" data-id="element-4893" data-mp="14cu7m7tp5rub">Donate Now</Button>
                </Link>
                <Link to="/en/partnership/become-a-partner" data-id="element-4894" data-mp="18c325ewk9tqx">
                  <Button variant="secondary" data-id="element-4895" data-mp="ulr9new2dsz9">Become A Partner</Button>
                </Link>
              </div>
            </div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7
          }} className="lg:col-span-6 relative" data-id="element-4896" data-mp="zk15lrpm4xlx">
              <div className="relative w-full lg:w-[calc(100%+6rem)] lg:-ml-8 aspect-[200/148]" data-id="element-4897" data-mp="21dkndu7ibcz7">
                <MosqueArchMask src="https://cdn.magicpatterns.com/uploads/hvhTdNSGMR8GbT9gqPkVY1/Partnership.jpg" alt="Partners shaking hands in front of the Islamic Learning Hub signage in a modern office" data-id="element-4898" data-mp="1xcixw53e6faf" />
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Choose how you support */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-sage-50/60" data-id="element-4899" data-mp="1ehkhczvce093">
        <PatternDecor side="right" y="top" size="lg" data-id="element-4900" data-mp="1sgk585b16b47" />
        <Container data-id="element-4901" data-mp="1nfwdtiiw3s9g">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4902" data-mp="1nfeay31q2fsk">
            Choose how you support
          </h2>
          <div className="mt-10 grid md:grid-cols-2 gap-5" data-id="element-4903" data-mp="33w8syyw3ze62">
            {[{
            title: 'Donate for Children',
            body: 'Education is the most sustainable form of protection for Muslim children growing up in minority contexts.',
            cta: 'Donate',
            to: '/en/partnership/donate-for-children'
          }, {
            title: 'Scholarships Program',
            body: 'Scholarships ensure equal access and educational continuity for families facing hardship — with transparent allocation and reporting.',
            cta: 'Explore scholarships',
            to: '/en/partnership/scholarships'
          }, {
            title: 'Institutional Partners',
            body: 'Multi-year, scalable initiatives with clear governance, ethical oversight, and impact reporting for foundations and ministries.',
            cta: 'Request partner brief',
            to: '/en/partnership/institutional-partners'
          }, {
            title: 'Become a Partner',
            body: 'We partner with institutions and experts who prioritize responsibility, long-term sustainability, and measurable impact.',
            cta: 'Apply to partner',
            to: '/en/partnership/become-a-partner'
          }].map((card, i) => <motion.div key={card.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.06,
            ease: [0.22, 1, 0.36, 1]
          }} whileHover={{
            y: -2
          }} className="rounded-2xl" data-id="element-4904" data-mp="3lkxae1me9dj0" data-mpi={i}>
                <Link to={card.to} className={`group block h-full rounded-2xl border border-ink-900/5 p-6 shadow-soft flex flex-col transition-shadow hover:shadow-card ${['bg-sage-100', 'bg-[#F7EDE9]', 'bg-sand-100', 'bg-[#EDF2F7]'][i]}`} data-id="element-4905" data-mp="111rned6wb4u0">
                  <h3 className="font-display text-lg text-ink-900" data-id="element-4906" data-mp="1b6hropprul1e">
                    {card.title}
                  </h3>
                  <p className="mt-3 text-ink-700 text-sm leading-relaxed flex-1" data-id="element-4907" data-mp="1f68i2sbdyz5e">
                    {card.body}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-forest-700 font-medium text-sm" data-id="element-4908" data-mp="1686k6lmy8mmc">
                    {card.cta}
                    <ArrowRightIcon size={16} strokeWidth={1.5} className="transition-transform group-hover:translate-x-0.5" data-id="element-4909" data-mp="s9yftsnl2j15" />
                  </span>
                </Link>
              </motion.div>)}
          </div>
        </Container>
      </section>

      {/* Partnership models */}
      <section className="py-16 md:py-24 bg-white/60" data-id="element-4910" data-mp="f17o2zld5dux">
        <Container data-id="element-4911" data-mp="az30ahhljbg7">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4912" data-mp="azm998p9qpi3">
            Partnership models
          </h2>
          <p className="mt-4 text-ink-700" data-id="element-4913" data-mp="oriucssirz4z">
            We work with donors, institutions, and organizations who share our
            commitment to Muslim families worldwide.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5" data-id="element-4914" data-mp="ymucll34zohh">
            {[{
            icon: HeartIcon,
            title: 'Individual Donors',
            text: 'Recurring or one-time gifts that fund scholarships, materials, and program delivery.',
            tone: 'bg-sage-100'
          }, {
            icon: LandmarkIcon,
            title: 'Institutional Partners',
            text: 'NGOs, foundations, and government partners working together on long-term impact projects.',
            tone: 'bg-[#EDF2F7]'
          }, {
            icon: BriefcaseIcon,
            title: 'Corporate / CSR',
            text: 'CSR partnerships from organizations whose core business is consistent with Islamic ethical principles.',
            tone: 'bg-sand-100'
          }].map(p => <div key={p.title} className={`rounded-2xl ${p.tone} border border-ink-900/5 p-6 shadow-soft`} data-id="element-4915" data-mp="2xcazzq1tluwp">
                <span className="w-11 h-11 inline-flex items-center justify-center rounded-full bg-white border border-forest-700/10 text-forest-700" data-id="element-4916" data-mp="24nnbzmposrfg">
                  <p.icon size={18} strokeWidth={1.6} data-id="element-4917" data-mp="29pl2n20f2oif" />
                </span>
                <h3 className="mt-5 font-display text-lg text-ink-900" data-id="element-4918" data-mp="2eo622z7uh45t">
                  {p.title}
                </h3>
                <p className="mt-2 text-ink-700 text-sm leading-relaxed" data-id="element-4919" data-mp="3l4rmqdwi6b2c">
                  {p.text}
                </p>
              </div>)}
          </div>
        </Container>
      </section>

      {/* How we report */}
      <section className="py-16 md:py-24 bg-sand-50/60" data-id="element-4920" data-mp="19es314wwcbhv">
        <Container data-id="element-4921" data-mp="19helw0p8mdfe">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4922" data-mp="14olhxf1lf94d">
            How we report
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-4923" data-mp="zqa834basym5">
            We provide clear reporting and documentation designed for
            institutional donors and long-term partners.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5" data-id="element-4924" data-mp="qokf3u1rprm9">
            {[{
            title: 'Quarterly impact reporting',
            body: 'Progress, outputs, and outcomes — structured for partner review and stakeholder updates.'
          }, {
            title: 'Annual financial summary',
            body: 'Clear overview of fund allocation and program costs (audit-ready where applicable).'
          }, {
            title: 'Governance & safeguarding',
            body: 'Policies, roles, and safeguards that support responsible delivery and accountability.'
          }].map((card, i) => <motion.div key={card.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.06
          }} className={`rounded-2xl border border-ink-900/5 p-6 shadow-soft ${['bg-sage-100', 'bg-[#F7EDE9]', 'bg-sand-100'][i]}`} data-id="element-4925" data-mp="2t2zv4o83kbtp" data-mpi={i}>
                <h3 className="font-display text-lg text-ink-900" data-id="element-4926" data-mp="1hlgiy6cjgyj0">
                  {card.title}
                </h3>
                <p className="mt-3 text-ink-500 text-sm leading-relaxed" data-id="element-4927" data-mp="3obtnh0crb7s1">
                  {card.body}
                </p>
              </motion.div>)}
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-white/60" data-id="element-4928" data-mp="3eef8742kkp1b">
        <PatternDecor side="left" y="bottom" size="md" data-id="element-4929" data-mp="3jc7beu8nzxkt" />
        <Container data-id="element-4930" data-mp="3jantkz7d1egx">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8" data-id="element-4931" data-mp="awxahpgigyem">
            <div className="max-w-xl" data-id="element-4932" data-mp="av40c06ovw1v">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4933" data-mp="fw9v03jwtte1">
                Ready to support?
              </h2>
              <p className="mt-4 text-ink-700" data-id="element-4934" data-mp="1ybjr70vyd2r">
                Choose the most relevant option — donation, scholarships, or
                institutional partnership. We'll respond quickly with next
                steps.
              </p>
            </div>
            <div className="flex flex-col items-start md:items-end gap-3" data-id="element-4935" data-mp="2qp4ci102tku1">
              <p className="max-w-sm text-sm text-ink-500 leading-relaxed md:text-right" data-id="element-4936" data-mp="x5voxp08dl98">
                Please note: We accept Sadaqah and corporate grants; Zakat is
                not accepted at this stage.
              </p>
              <div className="flex flex-wrap gap-3" data-id="element-4937" data-mp="ne83c4qcuyfo">
                <Link to="/en/contact#general-inquiry" data-id="element-4938" data-mp="igg0ifo69rwp">
                  <Button variant="primary" data-id="element-4939" data-mp="ifwt3sw5elwx">Donate</Button>
                </Link>
                <Link to="/en/contact#general-inquiry" data-id="element-4940" data-mp="efw1t7nyag8g">
                  <Button variant="secondary" data-id="element-4941" data-mp="9fro1peffzlx">Become a Partner</Button>
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>;
}